import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import EditableText from './EditableText';

const testimonials = [
  {
    quote: "Thanks Jeff! I am extremely please with our results we have gained so far and have garnered additional interest from other companies that have heard of our success using your program.",
    author: "Chris P.",

  },
  {
    quote: "Amber, Thank you so much for your after hours help. You have saved the day once again.",
    author: "Daniel G.",

  },
  {
    quote: "Good morning Ben. Just a quick note to let you know that voting ended Tuesday evening and results were final yesterday. I won re-election to my board position with about 60% of the votes! Hard to know the true effectiveness of any single activity in a campaign, but I would use your voter data service and text messaging service without hesitation, and would recommend it to any of my peers who may need it. You were knowledgeable, responsive, patient, and you delivered as promised on short notice every time I called. Thank you for your service and support — I sincerely believe it was a significant contributor to my success. ",
    author: "Robert S.",
  },
  {
    quote:"Happy turkey week, all! Very thankful for the opportunity to learn from you all here in the wild & wonderful world of P2P :)",
    author: "Danielle S.",
  },
  {
    quote:"Hi Amber & Rachele, Adam and I just wanted to say a huge thank you for the amazing Halloween Goodie Box, you two are simply the best! 🎃 Everything was so thoughtful and fun. As you can see in the second picture, I still have the little spider from last year, he's my all-year Halloween tradition! We're so grateful for your friendship and partnership. ",
    author: "Robert F.",
  },
  {
    quote:"Hi Ashley, I hope you had a nice weekend! Thank you so much for digging into this. We really appreciate your help and I know TPM appreciates it too. We're so grateful for your friendship and partnership. I hope you have a great day, and please don't hesitate if you need anything.",
    author: "Ryan F.",
  }
];

const About = () => {
  const aboutSectionRef = useRef(null);
  const scrollPathsRef = useRef(null);
  const scrollPathsExtendedRef = useRef(null);
  const missionValuesWrapperRef = useRef(null);
  const mobilePathHeartRef = useRef(null);
  const mobilePathZigzagRef = useRef(null);
  const mobilePathObserverRef = useRef(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonialIntervalRef = useRef(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [slideDirection, setSlideDirection] = useState('left'); // 'left' or 'right'
  const isResettingRef = useRef(false);

  useEffect(() => {
    // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger);

    // Apply reveal animation to section title
    const sectionTitle = document.querySelector('#about h2');
    if (sectionTitle) {
      sectionTitle.classList.add('reveal');
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      });
      revealObserver.observe(sectionTitle);
    }

    // Apply stagger animation to about grid
    const aboutGrid = document.querySelector('[data-about-grid]');
    if (aboutGrid) {
      const items = aboutGrid.querySelectorAll('[data-value-card], [data-mission-card]');
      items.forEach(item => {
        item.classList.add('reveal-stagger');
      });
      const staggerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const group = entry.target;
            const items = group.querySelectorAll('.reveal-stagger');
            items.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('revealed');
              }, index * 150);
            });
            staggerObserver.unobserve(group);
          }
        });
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      });
      staggerObserver.observe(aboutGrid);
    }

    // Scroll paths animation - wait for DOM to be ready; skip on mobile (paths hidden there)
    const initScrollPaths = () => {
      if (!aboutSectionRef.current || window.innerWidth < 768) return;

      const useExtended = window.innerWidth < 1399;
      const container = useExtended ? scrollPathsExtendedRef.current : scrollPathsRef.current;
      if (!container) return;
      const scrollPaths = container.querySelectorAll('.scroll-path');

      if (scrollPaths.length === 0) return;

      // Kill any existing ScrollTriggers for this section
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars && trigger.vars.trigger === aboutSectionRef.current) {
          trigger.kill();
        }
      });

      const masterTL = gsap.timeline({
        scrollTrigger: {
          trigger: aboutSectionRef.current,
          start: 'top 80%',
          end: 'bottom 20%',
          scrub: 0.01,
        }
      });

      scrollPaths.forEach((scrollPath, index) => {
        const pathLength = scrollPath.getTotalLength();
        if (pathLength === 0) return; // Skip if path has no length
        
        scrollPath.style.strokeDasharray = pathLength;
        scrollPath.style.strokeDashoffset = pathLength;

        if (index === 0) {
          // Main path - should complete before split paths start
          scrollPath.style.opacity = '1';
          masterTL.to(scrollPath, {
            strokeDashoffset: 0,
            duration: 0.1,
            ease: 'none'
          }, 0);
        } else {
          // Split paths - start after main path completes, but animate faster
          scrollPath.style.opacity = '1';
          masterTL.to(scrollPath, {
            strokeDashoffset: 0,
            duration: 0.3,
            ease: 'none'
          }, 0.16);
        }
      });
    };

    // Clock animation
    const hourHandGroup = document.getElementById('hour-hand-group');
    const minuteHandGroup = document.getElementById('minute-hand-group');
    let clockAnimationInterval = null;
    let clockObserver = null;

    if (hourHandGroup && minuteHandGroup) {

      let currentAngle = {
        hour: 0,
        minute: 0
      };
      let rotationProgress = 0;
      const targetRotations = 3 * 360;
      const fastSpeed = 15;
      let isSpinning = false;
      let lastTickTime = Date.now();
      let hasAnimated = false;

      const updateClock = () => {
        if (isSpinning) {
          currentAngle.minute -= fastSpeed;
          currentAngle.hour -= fastSpeed / 12;
          rotationProgress += fastSpeed;

          if (rotationProgress >= targetRotations) {
            isSpinning = false;
            currentAngle.minute = Math.round(currentAngle.minute / 6) * 6;
            currentAngle.hour = Math.round(currentAngle.hour / 30) * 30;
          }
        } else {
          const now = Date.now();
          if (now - lastTickTime >= 1000) {
            currentAngle.minute += 6;
            currentAngle.hour += 0.5;
            lastTickTime = now;
          }
        }

        if (hourHandGroup) {
          hourHandGroup.setAttribute('transform', `translate(280, 280) rotate(${currentAngle.hour})`);
        }
        if (minuteHandGroup) {
          minuteHandGroup.setAttribute('transform', `translate(280, 280) rotate(${currentAngle.minute})`);
        }
      };

      const startClockAnimation = () => {
        if (hasAnimated) return;
        hasAnimated = true;
        isSpinning = true;
        rotationProgress = 0;
      };

      clockObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasAnimated) {
            startClockAnimation();
          }
        });
      }, { threshold: 0.1 });

      const aboutSection = aboutSectionRef.current;
      if (aboutSection) {
        clockObserver.observe(aboutSection);
      }

      clockAnimationInterval = setInterval(updateClock, 16);
    }

    // Initialize scroll paths after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      initScrollPaths();
    }, 100);

    // Mobile-only: path draw progress from scroll position. Use actual path length and rAF loop
    // so we're not dependent on scroll events (which can be throttled on mobile).
    const initMobilePathAnimation = () => {
      if (window.innerWidth >= 768) return;
      const wrapper = missionValuesWrapperRef.current;
      const heartEl = mobilePathHeartRef.current;
      const zigzagEl = mobilePathZigzagRef.current;
      if (!wrapper || !heartEl || !zigzagEl) return;

      heartEl.style.strokeDasharray = '1';
      heartEl.style.strokeDashoffset = '1';
      zigzagEl.style.strokeDasharray = '1';
      zigzagEl.style.strokeDashoffset = '1';

      let rafId = null;
      let cancelled = false;
      const updatePathFromScroll = () => {
        if (cancelled) return;
        const vh = window.innerHeight;
        const rect = wrapper.getBoundingClientRect();
        const top = rect.top;
        const startY = vh;
        const endY = -vh * 2;
        let progress = 0;
        if (top >= startY) progress = 0;
        else if (top <= endY) progress = 1;
        else progress = 1 - (top - endY) / (startY - endY);
        progress = Math.max(0, Math.min(1, progress));
        // Heart finishes first (first 35% of scroll), then zigzag draws quicker (finishes by 80% of scroll)
        const heartPortion = 0.35;
        const zigzagEnd = 0.8;
        const heartProgress = progress <= heartPortion ? progress / heartPortion : 1;
        const zigzagProgress = progress <= heartPortion ? 0 : progress >= zigzagEnd ? 1 : (progress - heartPortion) / (zigzagEnd - heartPortion);
        heartEl.style.strokeDashoffset = String(1 - heartProgress);
        zigzagEl.style.strokeDashoffset = String(1 - zigzagProgress);
      };

      const tick = () => {
        if (cancelled) return;
        updatePathFromScroll();
        rafId = requestAnimationFrame(tick);
      };

      updatePathFromScroll();
      rafId = requestAnimationFrame(tick);

      const teardown = () => {
        cancelled = true;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
      };
      mobilePathObserverRef.current = { teardown };
    };
    const runMobilePathIfNeeded = () => {
      if (window.innerWidth < 768) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => initMobilePathAnimation());
        });
      }
    };
    const mobilePathTimeoutId = setTimeout(runMobilePathIfNeeded, 400);

    // Re-init scroll paths when resizing
    const handleResize = () => {
      mobilePathObserverRef.current?.teardown?.();
      mobilePathObserverRef.current = null;
      if (window.innerWidth >= 768) {
        initScrollPaths();
      } else {
        initMobilePathAnimation();
      }
    };
    window.addEventListener('resize', handleResize);

    // Capture ref value for cleanup
    const aboutSectionElement = aboutSectionRef.current;

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(mobilePathTimeoutId);
      window.removeEventListener('resize', handleResize);
      mobilePathObserverRef.current?.teardown?.();
      mobilePathObserverRef.current = null;
      // Cleanup ScrollTriggers (desktop + mobile path)
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars?.trigger === aboutSectionElement || trigger.vars?.id === 'mobile-mission-path') {
          trigger.kill();
        }
      });
      // Cleanup clock animation
      if (clockAnimationInterval) {
        clearInterval(clockAnimationInterval);
      }
      if (clockObserver) {
        clockObserver.disconnect();
      }
    };
  }, []);

  // Testimonial auto-advance (only if user hasn't interacted)
  useEffect(() => {
    if (hasUserInteracted) return;
    
    testimonialIntervalRef.current = setInterval(() => {
      setCurrentTestimonial((prev) => {
        const next = (prev + 1) % testimonials.length;
        // If wrapping, continue to duplicate
        if (next === 0 && prev === testimonials.length - 1) {
          return prev + 1; // testimonials.length
        }
        return next;
      });
    }, 5000);
    
    return () => {
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
      }
    };
  }, [hasUserInteracted]);


  const nextTestimonial = () => {
    // Stop auto-advance when user interacts
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
      }
    }
    setSlideDirection('left');
    setCurrentTestimonial((prev) => {
      const next = (prev + 1) % testimonials.length;
      // If wrapping from last to first, continue to duplicate (seamless loop)
      if (next === 0 && prev === testimonials.length - 1) {
        return prev + 1; // Continue to duplicate (testimonials.length)
      }
      return next;
    });
  };

  const prevTestimonial = () => {
    // Stop auto-advance when user interacts
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
      }
    }
    setCurrentTestimonial((prev) => {
      const newIndex = (prev - 1 + testimonials.length) % testimonials.length;
      // If wrapping from first to last, jump to duplicate, then animate
      if (prev === 0 && newIndex === testimonials.length - 1) {
        setSlideDirection('right');
        // Jump to duplicate position instantly
        setTimeout(() => {
          setCurrentTestimonial(testimonials.length + newIndex);
          // Then animate to target
          setTimeout(() => {
            setCurrentTestimonial(newIndex);
            setSlideDirection('left');
          }, 10);
        }, 0);
        return testimonials.length + newIndex;
      }
      // For normal backward navigation, jump to duplicate then animate
      setSlideDirection('right');
      setTimeout(() => {
        setCurrentTestimonial(testimonials.length + newIndex);
        setTimeout(() => {
          setCurrentTestimonial(newIndex);
          setSlideDirection('left');
        }, 10);
      }, 0);
      return testimonials.length + newIndex;
    });
  };

  return (
    <section
      id="about"
      ref={aboutSectionRef}
      className="pt-4 md:pt-6 lg:pt-8 pb-8 md:pb-10 lg:pb-12 px-4 sm:px-6 lg:px-8 relative z-[2] bg-[var(--bg-white)] overflow-hidden w-full max-w-[100vw]"
      style={{
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(122, 91, 109, 0.02) 2px, rgba(122, 91, 109, 0.02) 4px),
          repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(79, 49, 61, 0.015) 2px, rgba(79, 49, 61, 0.015) 4px)
        `,
        backgroundSize: '40px 40px'
      }}
    >
      {/* Max-width wrapper: scroll paths and content share same constrained width */}
      <div className="!max-w-[1520px] mx-auto relative w-full">
      {/* Scroll paths SVG - hidden on mobile; at 1399px+ use shorter main path (split at 1130) */}
      <div
        ref={scrollPathsRef}
        className="hidden desktopPath:block absolute -top-[50px] left-0 w-full h-[1600px] overflow-visible z-[1] pointer-events-none"
      >
        <svg className="w-full h-full block overflow-visible mx-auto" viewBox="-280 0 1400 1600" preserveAspectRatio="none">
          {/* Main wavy scroll path - flows from top center, curves down and splits */}
          <path
            className="scroll-path scroll-path-main"
            d="M500,0 Q470,160 470,320 Q470,480 540,550 Q620,620 760,610 Q900,600 990,500 Q1080,400 1050,250 Q1020,100 900,80 C850,70 700,80 650,120 C600,160 550,220 520,320 C490,420 480,560 490,720 C500,880 480,960 460,1020 C445,1060 430,1090 420,1100 L420,1130"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="20"
            strokeLinecap="round"
          />
          {/* Split path 1 - far left */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1130
              L -100 1200
              C -100 1240, -100 1280, -100 1320
              C -100 1360, -100 1400, -100 1440
              L -100 1470"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 2 - left */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1130
               L 150 1200
               C 150 1240, 150 1280, 150 1320
               C 150 1360, 150 1400, 150 1440
               L 150 1470"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 3 - center */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1130
               L 420 1200
               C 420 1240, 420 1280, 420 1320
               C 420 1360, 420 1400, 420 1440
               L 420 1470"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 4 - right */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1130
               L 680 1200
               C 680 1240, 680 1280, 680 1320
               C 680 1360, 680 1400, 680 1440
               L 680 1470"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 5 - far right */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1130
               L 920 1200
               C 920 1240, 920 1280, 920 1320
               C 920 1360, 920 1400, 920 1440
               L 920 1470"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Scroll paths SVG - from md to 1398px main path extends further down before split (split at 1250) */}
      <div ref={scrollPathsExtendedRef} className="hidden md:block desktopPath:hidden absolute -top-[50px] left-0 w-full h-[1600px] overflow-visible z-[1] pointer-events-none">
        <svg className="w-full h-full block overflow-visible mx-auto" viewBox="-280 0 1400 1600" preserveAspectRatio="none">
          {/* Main path extended down to 1250 before split */}
          <path
            className="scroll-path scroll-path-main"
            d="M500,0 Q470,160 470,320 Q470,480 540,550 Q620,620 760,610 Q900,600 990,500 Q1080,400 1050,250 Q1020,100 900,80 C850,70 700,80 650,120 C600,160 550,220 520,320 C490,420 480,560 490,720 C500,880 480,960 460,1020 C445,1060 430,1090 420,1100 L420,1130 L420,1250"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="20"
            strokeLinecap="round"
          />
          {/* Split path 1 - far left */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1250
              L -100 1320
              C -100 1360, -100 1400, -100 1440
              C -100 1480, -100 1520, -100 1560
              L -100 1590"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 2 - left */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1250
               L 150 1320
               C 150 1360, 150 1400, 150 1440
               C 150 1480, 150 1520, 150 1560
               L 150 1590"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 3 - center */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1250
               L 420 1320
               C 420 1360, 420 1400, 420 1440
               C 420 1480, 420 1520, 420 1560
               L 420 1590"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 4 - right */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1250
               L 680 1320
               C 680 1360, 680 1400, 680 1440
               C 680 1480, 680 1520, 680 1560
               L 680 1590"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Split path 5 - far right */}
          <path
            className="scroll-path scroll-path-circle"
            d="M 420 1250
               L 920 1320
               C 920 1360, 920 1400, 920 1440
               C 920 1480, 920 1520, 920 1560
               L 920 1590"
            fill="none"
            stroke="#9a83ad"
            strokeWidth="16"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Main content container */}
      <div className="relative overflow-visible w-full">
        {/* On mobile: title + clock in a row; on lg: clock is absolute top-right */}
        <div className="flex flex-row items-center justify-between gap-3 lg:contents">
        {/* Section Title - on mobile: shifted down a lot */}
        <h2 className="text-4xl sm:text-[2.5rem] md:text-5xl lg:text-6xl xl:text-7xl mb-4 md:mb-6 text-left border-b-0 pb-0 relative font-serif text-black max-w-full overflow-visible whitespace-nowrap after:content-[''] after:absolute after:-bottom-[10px] after:left-0 after:w-[150px] sm:w-[200px] md:w-[250px] after:h-[4px] sm:after:h-[5px] md:after:h-[6px] after:bg-[var(--accent-color)] after:transform-none mt-24 md:mt-0 pt-0">
          <EditableText path="about.title" fallback="About Us" />
        </h2>

        {/* Clock - on mobile: to the right of title; on md: bigger in row; on lg: large, absolute top-right (sizes match refactor/convert-to-react: 280→350→420) */}
        <div className="flex-shrink-0 w-40 h-40 md:w-[350px] md:h-[350px] lg:block lg:absolute lg:top-20 lg:right-[5%] xl:right-[8%] 2xl:right-[8%] reviewsLg:top-4 lg:w-[420px] lg:h-[420px] xl:w-[420px] xl:h-[420px] 2xl:w-[420px] 2xl:h-[420px] z-[10] p-0 lg:p-2 xl:p-4">
          <svg
            className="block drop-shadow-[0_8px_16px_rgba(0,0,0,0.3)] overflow-hidden w-full h-full"
            viewBox="0 0 560 560"
          >
            <defs>
              <filter id="clock-shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="rgba(0,0,0,0.5)"/>
                <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="rgba(0,0,0,0.3)"/>
              </filter>
              <linearGradient id="clock-ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2a2a2a" />
                <stop offset="50%" stopColor="#1a1a1a" />
                <stop offset="100%" stopColor="#0a0a0a" />
              </linearGradient>
            </defs>
            {/* Clock face - bigger and 3D */}
            <circle
              cx="280"
              cy="280"
              r="240"
              fill="none"
              stroke="url(#clock-ring-gradient)"
              strokeWidth="12"
              filter="url(#clock-shadow)"
            />
            {/* Inner highlight for 3D effect */}
            <circle
              cx="280"
              cy="280"
              r="240"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="2"
              strokeDasharray="20 10"
            />
            {/* Hour markers */}
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((hour) => {
              const angle = (hour * 30 - 90) * (Math.PI / 180);
              const x1 = 280 + 200 * Math.cos(angle);
              const y1 = 280 + 200 * Math.sin(angle);
              const x2 = 280 + 220 * Math.cos(angle);
              const y2 = 280 + 220 * Math.sin(angle);
              return (
                <line
                  key={hour}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="#1a1a1a"
                  strokeWidth="6"
                />
              );
            })}
            {/* Hour hand - centered at 280, 280 */}
            <g id="hour-hand-group" transform="translate(280, 280)">
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="-100"
                stroke="#9a83ad"
                strokeWidth="8"
                strokeLinecap="round"
              />
            </g>
            {/* Minute hand - centered at 280, 280 */}
            <g id="minute-hand-group" transform="translate(280, 280)">
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="-160"
                stroke="#666666"
                strokeWidth="6"
                strokeLinecap="round"
              />
            </g>
            {/* Center dot */}
            <circle cx="280" cy="280" r="12" fill="#9a83ad" />
          </svg>
        </div>
        </div>

        {/* Our Story Card - on mobile: shifted down, slightly up from before */}
        <div className="bg-[var(--bg-dark)] p-5 sm:p-6 md:p-8 lg:p-10 mb-4 md:mb-6 lg:mb-8 border border-[var(--border-dark)] shadow-md w-full md:w-[60%] lg:w-1/2 max-w-[700px] rounded-xl relative mt-10 md:mt-0">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold uppercase tracking-[2px] mb-4 md:mb-5 text-[var(--bg-pure-white)] font-sans border-b-2 border-[var(--accent-color)] pb-2 md:pb-3">
            <EditableText path="about.ourStoryTitle" fallback="Our Story" />
          </h3>
          <div className="text-[var(--bg-pure-white)] leading-[1.6] text-sm sm:text-base md:text-[1.05rem] font-sans">
            <p className="mb-3 md:mb-4">
            <EditableText path="about.storyPara1" fallback="For nearly four decades, The VBC Group has partnered with organizations doing impactful work to ensure their messages reach the people who matter most. Built on a foundation of trusted outreach services dating back to 1987, we bring together experience, innovation, and human-centered communication under one unified brand." />
            </p>
            <p className="mb-3 md:mb-4">
            What sets The VBC Group apart is our belief that{' '}
            <span className="text-[var(--accent-color)] font-semibold inline leading-[1.6]"><EditableText path="about.storyPara2Highlight" fallback="effective communication is both powerful and personal." /></span>{' '}
            <EditableText path="about.storyPara2Rest" fallback="We don't just deliver messages — we help our clients ask better questions, hear real answers, and turn insight into action. By combining proven technology with hands-on partnership, we make it easier to reach audiences clearly, gather meaningful feedback, and drive measurable results at scale." />
            </p>
            <p className="mb-3 md:mb-4">
            <EditableText path="about.storyPara3" fallback="We've supported millions of outreach efforts and delivered billions of communications. But our focus has never been just volume. It's about clarity." />{' '}
            <span className="text-[var(--accent-color)] font-semibold inline leading-[1.6]"><EditableText path="about.storyPara3Highlight" fallback="It's about trust." /></span>
            </p>

            <p className="mb-0">
            <EditableText path="about.storyPara4" fallback="Today, The VBC Group serves as a strategic communications partner for organizations that need reliability, responsiveness, and results — all backed by a team that understands the responsibility that comes with amplifying a voice." />
            </p>
          </div>
        </div>

        {/* On mobile: Our Customer Reviews first, then Mission; on desktop: Mission and Reviews side-by-side, then Values below */}
        <div className="flex flex-col lg:flex-row lg:flex-wrap">
        {/* Our Customer Reviews - on mobile: below Our Story; on desktop: right column next to Mission */}
        <div className="order-1 md:order-2 lg:order-2 mb-8 md:mb-10 lg:mb-0 w-full md:w-[50%] lg:w-[40%] max-w-[580px] relative flex flex-col items-center gap-3 ml-auto mt-4 md:-mt-[280px] lg:-mt-[260px] reviewsLg:-mt-[140px] z-[10]">
          <h2 className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-bold uppercase tracking-[2px] sm:tracking-[3px] md:tracking-[4px] text-center text-[var(--text-dark)] mb-2 sm:mb-3 font-sans w-full">
            <EditableText path="about.customerReviewsTitle" fallback="OUR CUSTOMER REVIEWS" />
          </h2>

          {/* Testimonial Slideshow - on mobile: min-height so longest quote fits; on desktop: fixed height */}
          <div className="w-full relative overflow-hidden min-h-[520px] h-auto md:min-h-0 md:h-[360px]">
            <div 
              className="absolute inset-0 flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentTestimonial * 100}%)`,
                transition: slideDirection === 'right' ? 'none' : 'transform 500ms ease-in-out'
              }}
              onTransitionEnd={(e) => {
                // Only handle transform transitions
                if (e.propertyName === 'transform' && currentTestimonial >= testimonials.length && !isResettingRef.current) {
                  isResettingRef.current = true;
                  setSlideDirection('right'); // Disable transition
                  setCurrentTestimonial(currentTestimonial % testimonials.length);
                  // Re-enable transition on next frame
                  requestAnimationFrame(() => {
                    setSlideDirection('left');
                    isResettingRef.current = false;
                  });
                }
              }}
            >
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <div
                  key={index}
                  className="min-w-full h-full min-h-[520px] md:min-h-0 px-0"
                >
                  <div
                    className="bg-white border-4 border-[#9a83ad] rounded-xl p-4 md:p-5 shadow-lg relative overflow-hidden min-h-[520px] md:min-h-0 md:h-[360px] flex flex-col"
                    style={{
                      backgroundImage: `
                        repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(223, 192, 181, 0.03) 1px, rgba(223, 192, 181, 0.03) 2px)
                      `,
                      backgroundSize: '20px 20px'
                    }}
                  >
                    <div className="absolute top-3 left-3 text-4xl text-[#9a83ad] opacity-20 font-serif">&ldquo;</div>
                    <div className="relative z-10 flex-1 flex flex-col overflow-hidden items-center">
                      <div className="flex-1 flex items-center justify-center">
                        <p className="text-[var(--text-dark)] text-sm md:text-base leading-relaxed font-serif overflow-y-auto text-center px-4">
                          <span className="text-[#9a83ad] text-xl font-serif">&ldquo;</span>
                          {testimonial.quote}
                          <span className="text-[#9a83ad] text-xl font-serif">&rdquo;</span>
                        </p>
                      </div>
                      <div className="border-t-2 border-[#9a83ad] pt-2 pb-0 text-center flex-shrink-0 w-full">
                        <p className="font-semibold text-[var(--text-dark)] text-base md:text-lg mb-0.5">
                          {testimonial.author}
                        </p>
                        {testimonial.role && (
                          <p className="text-xs text-gray-600 mb-0.5">
                            {testimonial.role}
                          </p>
                        )}
                        {testimonial.organization && (
                          <p className="text-xs text-[#9a83ad] font-semibold mb-0">
                            {testimonial.organization}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation buttons below card */}
          <div className="flex gap-4 mt-2 justify-center w-full">
            <button
              onClick={prevTestimonial}
              className="w-10 h-10 rounded-full bg-[#9a83ad] text-white flex items-center justify-center hover:bg-[#dfc0b5] transition-colors"
              aria-label="Previous testimonial"
            >
              ←
            </button>
            <button
              onClick={nextTestimonial}
              className="w-10 h-10 rounded-full bg-[#9a83ad] text-white flex items-center justify-center hover:bg-[#dfc0b5] transition-colors"
              aria-label="Next testimonial"
            >
              →
            </button>
          </div>
        </div>

        {/* Wrapper: on desktop lg:contents so Mission/Values become direct flex children for correct layout */}
        <div ref={missionValuesWrapperRef} className="relative order-2 md:order-1 lg:contents">
        {/* Mission section - on mobile: below Reviews; on desktop: left column (order-1), takes remaining space */}
        <div className="order-2 md:order-1 lg:order-1 lg:flex-1 lg:min-w-0 relative flex flex-col lg:flex-row items-start justify-start gap-4 lg:gap-6 mb-0">
          {/* Our Mission Card - positioned on the left; extra bottom space on mobile */}
          <div className="w-full lg:w-[50%] max-w-[700px] z-[10] bg-transparent p-0 border-0 shadow-none text-left mt-4 lg:mt-0 mr-auto mb-12 md:mb-0">
            <h2 className="text-5xl sm:text-5xl md:text-4xl lg:text-5xl xl:text-[5rem] font-extrabold md:font-bold uppercase tracking-[2px] sm:tracking-[3px] md:tracking-[4px] mb-3 sm:mb-4 md:mb-5 text-[var(--text-dark)] font-sans border-b-0 pb-0 leading-[1.2] max-w-full text-center md:text-left">
              <EditableText path="about.missionTitle" fallback="OUR MISSION" />
            </h2>
            <p className="text-base sm:text-lg md:text-xl lg:text-xl text-[var(--text-dark)] leading-[1.4] sm:leading-[1.5] font-sans m-0 font-medium w-full lg:w-[85%] text-center md:text-left">
              <EditableText path="about.missionTextBefore" fallback="We empower organizations doing impactful work by connecting them with their audiences through smart, " />
              <span className="text-[#9a83ad] font-semibold">reliable and responsive</span>
              <EditableText path="about.missionTextAfter" fallback=" outreach." />
            </p>
          </div>
        </div>

        {/* Our Values Title - on mobile: way more space above; on desktop: full width below Mission/Reviews with space above */}
        <div className="mt-32 sm:mt-36 md:mt-6 lg:mt-24 lg:order-3 lg:w-full mx-auto w-[90%] max-w-[1400px] relative z-[10] flex justify-center">
          <div className="bg-white rounded-xl px-5 sm:px-6 md:px-8 lg:px-10 py-3 sm:py-4 md:py-5 shadow-md">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[4rem] font-bold uppercase tracking-[2px] sm:tracking-[3px] md:tracking-[4px] text-center text-[var(--text-dark)] font-sans m-0">
              <EditableText path="about.valuesTitle" fallback="OUR VALUES" />
            </h2>
          </div>
        </div>

        {/* Value Cards - on mobile: alternate left/right; on desktop: full width below Values title, grid as before */}
        <div className="relative w-full max-w-[1400px] mt-3 sm:mt-4 md:mt-5 lg:mt-6 lg:order-4 lg:w-full mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 md:gap-6 z-[10] [&>*:nth-child(odd)]:justify-self-start [&>*:nth-child(even)]:justify-self-end md:[&>*]:justify-self-stretch" data-about-grid>
          <div 
            className="bg-[var(--bg-pure-white)] p-4 sm:p-5 md:p-6 border-4 border-[#9a83ad] rounded-xl text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[160px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center w-full max-w-[78%] md:max-w-none"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-sm sm:text-base mb-2 sm:mb-3 mt-0 px-3 sm:px-4 flex-shrink-0 min-h-[2rem] flex items-center justify-center font-serif font-bold text-center break-words"><EditableText path="about.valueIntegrity" fallback="INTEGRITY ALWAYS" className="text-[#9a83ad]" /></h3>
            <p className="text-sm sm:text-base leading-[1.5] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center"><EditableText path="about.valueIntegrityDesc" fallback="We uphold trust by staying true to our values, our word, and each other, even when it's hard, unseen, or unpopular." /></p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-4 sm:p-5 md:p-6 border-4 border-[#9a83ad] rounded-xl text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[160px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center w-full max-w-[78%] md:max-w-none"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-sm sm:text-base mb-2 sm:mb-3 mt-0 px-3 sm:px-4 flex-shrink-0 min-h-[2rem] flex items-center justify-center font-serif font-bold text-center break-words"><EditableText path="about.valueAmplify" fallback="AMPLIFY WITH PURPOSE" className="text-[#9a83ad]" /></h3>
            <p className="text-sm sm:text-base leading-[1.5] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center"><EditableText path="about.valueAmplifyDesc" fallback="We amplify voices—making them clearer, stronger, and more authentic." /></p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-4 sm:p-5 md:p-6 border-4 border-[#9a83ad] rounded-xl text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[160px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center w-full max-w-[78%] md:max-w-none"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-sm sm:text-base mb-2 sm:mb-3 mt-0 px-3 sm:px-4 flex-shrink-0 min-h-[2rem] flex items-center justify-center font-serif font-bold text-center break-words"><EditableText path="about.valueOwn" fallback="OWN THE OUTCOME" className="text-[#9a83ad]" /></h3>
            <p className="text-sm sm:text-base leading-[1.5] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center"><EditableText path="about.valueOwnDesc" fallback="We own challenges and successes alike, always striving for solutions." /></p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-4 sm:p-5 md:p-6 border-4 border-[#9a83ad] rounded-xl text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[160px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center w-full max-w-[78%] md:max-w-none"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-sm sm:text-base mb-2 sm:mb-3 mt-0 px-3 sm:px-4 flex-shrink-0 min-h-[2rem] flex items-center justify-center font-serif font-bold text-center break-words"><EditableText path="about.valueTogether" fallback="BETTER TOGETHER" className="text-[#9a83ad]" /></h3>
            <p className="text-sm sm:text-base leading-[1.5] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center"><EditableText path="about.valueTogetherDesc" fallback="We treat everyone—team members, clients, and partners—with respect, knowing great work happens through collaboration and positivity." /></p>
          </div>
          <div 
            className="bg-[var(--bg-pure-white)] p-4 sm:p-5 md:p-6 border-4 border-[#9a83ad] rounded-xl text-center transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer min-h-[160px] shadow-[0_4px_12px_rgba(154,131,173,0.1)] hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(154,131,173,0.25)] hover:border-[#9a83ad] flex flex-col justify-start items-center w-full max-w-[78%] md:max-w-none"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(122, 91, 109, 0.02) 1px, rgba(122, 91, 109, 0.02) 2px),
                repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(223, 192, 181, 0.015) 1px, rgba(223, 192, 181, 0.015) 2px)
              `,
              backgroundSize: '30px 30px'
            }}
          >
            <h3 className="text-[#9a83ad] text-sm sm:text-base mb-2 sm:mb-3 mt-0 px-3 sm:px-4 flex-shrink-0 min-h-[2rem] flex items-center justify-center font-serif font-bold text-center break-words"><EditableText path="about.valueSense" fallback="MAKE IT MAKE SENSE" className="text-[#9a83ad]" /></h3>
            <p className="text-sm sm:text-base leading-[1.5] text-[var(--text-dark)] m-0 flex-1 flex items-center justify-center"><EditableText path="about.valueSenseDesc" fallback="We make things work, simply, clearly, and effectively." /></p>
          </div>
        </div>

        {/* Mobile-only: connector path (heart + values zigzag). Heart is wider & less tall via transform. */}
        <div className="md:hidden absolute inset-0 pointer-events-none z-[2]" aria-hidden="true">
          <svg className="w-full h-full block" viewBox="0 0 300 520" preserveAspectRatio="none">
            <g transform="translate(150,130) scale(1.35,0.65) translate(-150,-130)">
              <path
                ref={mobilePathHeartRef}
                className="mobile-path-mission-values"
                d="M 143.4 111.5 C 141.9 108.5 138.2 105.8 135 103 C 128.6 94.1 121 86 125.7 75.6 C 127.9 70.9 140 71.8 144.9 74.3 C 151.9 77.9 152.8 82.2 152.8 88.4 C 152.8 88.9 152.8 87.7 152.8 87.3 C 152.8 86.3 152.9 85.5 152.9 84.6 C 153.5 82.3 155.2 79.9 157.1 78.1 C 163.8 74.5 180.3 72.9 183 79 C 188.6 92.9 165.2 97.5 156 107.5 C 149.1 114.7 150 121.5 150 130"
                pathLength="1"
                strokeDasharray="1"
                strokeDashoffset="1"
                fill="none"
                stroke="#9a83ad"
                strokeWidth="8"
                vectorEffect="nonScalingStroke"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <path
              ref={mobilePathZigzagRef}
              className="mobile-path-mission-values"
              d="M 150 130 L 150 155 Q 150 168 130 178 L 130 205 Q 130 225 170 248 L 170 275 Q 170 295 130 318 L 130 345 Q 130 365 170 388 L 170 415 Q 170 435 150 455 L 150 478 Q 130 498 110 518"
              pathLength="1"
              strokeDasharray="1"
              strokeDashoffset="1"
              fill="none"
              stroke="#9a83ad"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        </div>
        </div>
        
      </div>
      </div>
    </section>
  );
};

export default About;
